/**
 * Optimizer Control Center — the flows table (header + rows, with selection)
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';

import { Checkbox } from '@/components/ui';
import { optimizerRowKey } from '@/features/optimizer-center/clipboard';
import { OptimizerTableRow } from '@/features/optimizer-center/components/table/OptimizerTableRow';
import type { OptimizerRow } from '@/features/optimizer-center/optimizer-center.types';
import { cn } from '@/lib/utils';

const HEADERS: ReadonlyArray<{ label: string; className?: string }> = [
  { label: 'Provider' },
  { label: 'Country' },
  { label: 'Campaign' },
  { label: 'Flow', className: 'w-full' },
  { label: 'Mode' },
  { label: 'Status' },
  { label: 'Tag' },
  { label: 'Package' },
  { label: 'Interval' },
  { label: 'Next run' },
  { label: 'Last run' },
  { label: 'Formula' },
  { label: 'Actions' },
];

export interface OptimizerTableProps {
  rows: OptimizerRow[];
  nowMs: number;
  canEditSettings: boolean;
  onEdit: (row: OptimizerRow) => void;
  selectionEnabled: boolean;
  selected: Set<string>;
  copySourceKey: string | null;
  allSelected: boolean;
  someSelected: boolean;
  onToggleSelectAll: () => void;
  onToggleSelect: (key: string) => void;
  onCopy: (row: OptimizerRow) => void;
}

export const OptimizerTable: FC<OptimizerTableProps> = (props) => {
  const { rows, nowMs, canEditSettings, onEdit, selectionEnabled } = props;
  const { selected, copySourceKey, allSelected, someSelected } = props;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[1040px] border-collapse text-sm">
        <thead>
          <tr className="border-b bg-muted text-left text-[10px] uppercase tracking-wide text-muted-foreground">
            {selectionEnabled && (
              <th className="w-8 px-2 py-2">
                <Checkbox
                  aria-label="Select all flows"
                  checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                  onCheckedChange={props.onToggleSelectAll}
                />
              </th>
            )}
            {HEADERS.map((h) => (
              <th
                key={h.label}
                className={cn(
                  'px-2 py-2 font-semibold',
                  h.label === 'Actions' &&
                    'sticky right-0 z-20 bg-muted text-right shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.18)]',
                  h.className,
                )}
              >
                {h.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border/60">
          {rows.map((row) => {
            const key = optimizerRowKey(row);
            return (
              <OptimizerTableRow
                key={key}
                row={row}
                nowMs={nowMs}
                canEditSettings={canEditSettings}
                onEdit={onEdit}
                selectionEnabled={selectionEnabled}
                selected={selected.has(key)}
                isCopiedSource={copySourceKey === key}
                onToggleSelect={() => props.onToggleSelect(key)}
                onCopy={props.onCopy}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
